import axios from 'axios'
import { getToken, clearToken } from './token'
import { unpinMatter } from '../lib/pinnedMatters'
import { recentMattersTracker } from '../lib/recentItems'

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://iuris.mcmnxcloud.com'
})

// Automatically attach JWT token to every request (from local OR session storage)
api.interceptors.request.use(config => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle token expiry globally.
// IMPORTANT: Do NOT auto-redirect when the 401 came from /api/auth/login itself —
// that's the bad-credentials case, where we want the Login page's catch handler to
// render "Invalid email or password" instead of a silent reload that wipes state.
// Nor from /api/auth/logout: sign-out already clears and redirects itself, and a
// 401 there just means the session had already ended (logoutSession below).
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      const isLoginRequest = /\/api\/auth\/login/.test(url)
      const isLogoutRequest = /\/api\/auth\/logout(?:[?#]|$)/.test(url)
      if (!isLoginRequest && !isLogoutRequest) {
        clearToken()
        window.location.href = '/login'
      }
    }
    // 404 on a per-matter GET/PUT/DELETE = caller lost access (matter deleted OR
    // per-matter permission revoked OR visibility group flipped).  Scrub any
    // stale cached references so the sidebar / recents don't keep offering
    // a dead link the user can't open.  Best-effort — never break the error
    // propagation chain regardless of what happens in cleanup.
    if (error.response?.status === 404) {
      try {
        const url = error.response.config?.url || ''
        const m = url.match(/\/api\/matters\/(\d+)/)
        if (m) {
          const matterId = parseInt(m[1], 10)
          if (Number.isFinite(matterId)) {
            try { unpinMatter(matterId) } catch (_) { /* ignore */ }
            try { recentMattersTracker.remove(matterId) } catch (_) { /* ignore */ }
          }
        }
      } catch (_) { /* cleanup is best-effort — never rethrow */ }
    }
    return Promise.reject(error)
  }
)

// Auth
export const login = (email, password, remember = false) =>
  api.post('/api/auth/login', { email, password, remember })

export const verifyTotp = (totp_challenge_token, code) =>
  api.post('/api/auth/login/totp', { totp_challenge_token, code })

// Two-factor (TOTP) — Profile-page controls
export const twoFactorStatus = () => api.get('/api/profile/2fa/status')
export const twoFactorSetup = () => api.post('/api/profile/2fa/setup')
export const twoFactorEnable = (code) => api.post('/api/profile/2fa/enable', { code })
export const twoFactorDisable = (password) => api.post('/api/profile/2fa/disable', { password })
export const twoFactorRegenerateBackupCodes = (password) => api.post('/api/profile/2fa/regenerate-backup-codes', { password })

// Active session management — Profile-page controls
export const listSessions = () => api.get('/api/profile/sessions')
export const revokeSession = (id) => api.post(`/api/profile/sessions/${id}/revoke`)
export const revokeOtherSessions = () => api.post('/api/profile/sessions/revoke-others')
export const getLoginHistory = () => api.get('/api/profile/login-history')

// Data export audit trail — fire-and-forget after a PDF/Excel download.
// Writes an EXPORT row into audit_log so the admin Audit Log page can
// surface who exported what.  Failures are swallowed — logging must never
// block or delay a download.
export function logExport({ resource_type, resource_id, format, filename, description, count }) {
  try {
    // Don't await — fire and forget.  axios promise rejection is caught
    // by the .catch so a network failure never bubbles.
    api.post('/api/exports/log', {
      resource_type, resource_id, format, filename, description, count,
    }).catch(() => {})
  } catch (_) { /* ignore */ }
}

export const getMe = () =>
  api.get('/api/auth/me')

// Sign-out: end THIS sign-in's session on the server (POST /api/auth/logout),
// so a copy of the token stops working at once instead of living until it
// expires (up to 30 days).  AuthContext.logout calls it BEFORE clearing the
// token, because the request has to carry it.  Best-effort and bounded: it
// never rejects and settles within ~2 s (LOGOUT_TIMEOUT_MS, plus a 100 ms
// backstop) whatever happens — network error, timeout, 401 (already ended),
// 5xx — because signing out on
// this device must never wait on, or depend on, the server.  The 401
// interceptor above skips this URL, so a dead token cannot bounce through the
// /login redirect mid-sign-out.  → { ok, status }, plus skipped: true when no
// token is stored (nothing to end, no request sent).
export const LOGOUT_TIMEOUT_MS = 2000
export const logoutSession = () => {
  if (!getToken()) return Promise.resolve({ ok: false, status: null, skipped: true })
  let timer = null
  const cap = new Promise((resolve) => {
    timer = setTimeout(() => resolve({ ok: false, status: null }), LOGOUT_TIMEOUT_MS + 100)
  })
  let call
  try {
    call = api.post('/api/auth/logout', {}, { timeout: LOGOUT_TIMEOUT_MS })
      .then((res) => ({ ok: true, status: res.status }))
      .catch((err) => ({ ok: false, status: err?.response?.status || null }))
  } catch {
    call = Promise.resolve({ ok: false, status: null })
  }
  return Promise.race([call, cap]).finally(() => clearTimeout(timer))
}

// One-time ticket for the realtime WebSocket (hooks/useRealtime.js).  A
// browser cannot send an Authorization header on a WebSocket handshake, and a
// JWT in the socket URL lands in the web server's access log, so the socket
// URL carries this single-use, 30-second ticket instead.  → { ticket, expires_in }
export const getRealtimeTicket = () =>
  api.post('/api/ws/ticket')

// Clients
export const getClients = () =>
  api.get('/api/clients')

export const getClient = (id) =>
  api.get(`/api/clients/${id}`)

export const createClient = (data) =>
  api.post('/api/clients', data)

export const updateClient = (id, data) =>
  api.put(`/api/clients/${id}`, data)

export const deleteClient = (id) =>
  api.delete(`/api/clients/${id}`)

// Matters
//
// Wave 3 will migrate consumers off the default (with_wip) toward ?slim=true.
// Until then the default stays with_wip so no existing caller breaks.
//   getMatters()                → current shape (with_wip)
//   getMatters({slim: true})    → slim shape (no wip_amount / counts / total_costs)
//   getMatters({withWip: true}) → explicit with_wip (survives the Wave 3 default flip)
export const getMatters = (options = {}) => {
  const params = {}
  if (options.slim === true) params.slim = 'true'
  else if (options.withWip === true) params.with_wip = 'true'
  const qs = new URLSearchParams(params).toString()
  return api.get(`/api/matters${qs ? `?${qs}` : ''}`)
}

// Thin wrapper for the explicit "I need WIP totals" case.  Use this instead
// of getMatters() on any surface that reads .wip_amount / .unposted_count /
// .unbilled_count / .total_costs — it will keep working after Wave 3 flips
// the default to slim.
export const getMattersWithWip = () => getMatters({ withWip: true })

export const getMatter = (id) =>
  api.get(`/api/matters/${id}`)

export const getMattersByClient = (clientId) =>
  api.get(`/api/matters/client/${clientId}`)

export const createMatter = (data) =>
  api.post('/api/matters', data)

export const updateMatter = (id, data) =>
  api.put(`/api/matters/${id}`, data)

export const deleteMatter = (id) =>
  api.delete(`/api/matters/${id}`)

// Time Entries
export const getTimeEntries = () =>
  api.get('/api/time-entries')

export const getTimeEntriesByMatter = (matterId) =>
  api.get(`/api/time-entries/matter/${matterId}`)

export const getUnbilledTimeEntries = () =>
  api.get('/api/time-entries/unbilled')

export const createTimeEntry = (data) =>
  api.post('/api/time-entries', data)

export const updateTimeEntry = (id, data) =>
  api.put(`/api/time-entries/${id}`, data)

export const deleteTimeEntry = (id) =>
  api.delete(`/api/time-entries/${id}`)

// Invoices
export const getInvoices = (opts = {}) => {
  // Opt-in pagination — call getInvoices({ page, limit, q, status }) to
  // get { items, total, page, pages } back; call without args to get the
  // full array (backward-compat).
  const params = new URLSearchParams()
  if (opts.page != null) params.set('page', String(opts.page))
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.paginate) params.set('paginate', 'true')
  if (opts.q) params.set('q', opts.q)
  if (opts.status && opts.status !== 'all') params.set('status', opts.status)
  const qs = params.toString()
  return api.get(qs ? `/api/invoices?${qs}` : '/api/invoices')
}

export const getInvoice = (id) =>
  api.get(`/api/invoices/${id}`)

export const getInvoicesByMatter = (matterId) =>
  api.get(`/api/invoices/matter/${matterId}`)

export const createInvoice = (data) =>
  api.post('/api/invoices', data)

export const updateInvoice = (id, data) =>
  api.put(`/api/invoices/${id}`, data)

export const deleteInvoice = (id) =>
  api.delete(`/api/invoices/${id}`)

// Calendar
export const getEvents = () =>
  api.get('/api/calendar')

export const getUpcomingEvents = () =>
  api.get('/api/calendar/upcoming')

export const getEventsByMatter = (matterId) =>
  api.get(`/api/calendar/matter/${matterId}`)

export const getEventsByRange = (start, end) =>
  api.get(`/api/calendar/range?start=${start}&end=${end}`)

export const createEvent = (data) =>
  api.post('/api/calendar', data)

export const updateEvent = (id, data) =>
  api.put(`/api/calendar/${id}`, data)

export const deleteEvent = (id) =>
  api.delete(`/api/calendar/${id}`)

// Conflicts
export const runConflictCheck = (data) => api.post('/api/conflicts/check', data)
export const getConflictChecks = (page = 1) => api.get(`/api/conflicts?page=${page}`)
export const getConflictCheck = (id) => api.get(`/api/conflicts/${id}`)
export const clearConflict = (id, data) => api.put(`/api/conflicts/${id}/clear`, data)
export const deleteConflictCheck = (id) => api.delete(`/api/conflicts/${id}`)

// Feedback / Report Issue
export const submitFeedback = (data) => api.post('/api/feedback', data)
export const getFeedback = (params = {}) => {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) qs.append(k, v)
  const q = qs.toString()
  return api.get(`/api/feedback${q ? `?${q}` : ''}`)
}
export const updateFeedback = (id, data) => api.put(`/api/feedback/${id}`, data)
export const getFeedbackCounts = () => api.get('/api/feedback/counts')

// User groups (admin-managed) — used by Team page + matter visibility picker
// `{ includeMembers: true }` embeds each group's full member list in the one
// response instead of the caller fanning out to getGroup() per group.  It is
// ADMIN-ONLY server-side (403 otherwise) even though the bare list is open to
// all staff for the matter-visibility picker.
export const getGroups = (opts = {}) =>
  api.get('/api/team/groups' + (opts.includeMembers ? '?include=members' : ''))
export const getGroup  = (id) => api.get(`/api/team/groups/${id}`)
export const createGroup = (data) => api.post('/api/team/groups', data)
export const updateGroup = (id, data) => api.put(`/api/team/groups/${id}`, data)
export const deleteGroup = (id) => api.delete(`/api/team/groups/${id}`)

// Client merge tool (admin or can_delete_clients)
export const getClientDuplicates = () =>
  api.get('/api/clients/duplicates')
export const getClientMergePreview = (primaryId, duplicateId) =>
  api.get(`/api/clients/merge-preview?primary_id=${primaryId}&duplicate_id=${duplicateId}`)
export const mergeClients = (primaryId, duplicateId) =>
  api.post('/api/clients/merge', { primary_id: primaryId, duplicate_id: duplicateId })

// Contact + matter duplicate detection (admin-only for matters; admin/can_delete_contacts for contacts)
export const getContactDuplicates = () => api.get('/api/contacts/duplicates')
export const getMatterDuplicates = () => api.get('/api/matters/duplicates')
// Contact merge — dry-run preview + commit.
// preview → { primary, duplicate, reassignments, warnings, blockers, can_merge }.
// `reassignments` covers ALL eight tables that reference contacts(id), not just
// the two the merge used to know about.  `blockers` non-empty ⇒ POST /merge
// refuses with 409 (e.g. both contacts are active witness designations on one
// matter, or the duplicate is the vendor on general-ledger lines); `can_merge`
// is the boolean form.  merge commits + returns the counts + audit-logs as
// action=MERGE.
export const getContactMergePreview = (primary_id, duplicate_id) =>
  api.get('/api/contacts/merge-preview', { params: { primary_id, duplicate_id } })
export const mergeContacts = (primary_id, duplicate_id) =>
  api.post('/api/contacts/merge', { primary_id, duplicate_id })

// Attorney productivity dashboard v2 — per-attorney rollup + trend + top matters.
// Server-computed aggregates keyed on date range + optional focus/scope.
export const getAttorneyProductivity = (params = {}) =>
  api.get('/api/attorney-productivity', { params })

// Custom report builder — admin CRUD + user run.
export const getCustomReportSources = () => api.get('/api/custom-reports/sources')
export const listCustomReports = () => api.get('/api/custom-reports')
export const getCustomReport = (id) => api.get(`/api/custom-reports/${id}`)
export const createCustomReport = (payload) => api.post('/api/custom-reports', payload)
export const updateCustomReport = (id, payload) => api.put(`/api/custom-reports/${id}`, payload)
export const deleteCustomReport = (id) => api.delete(`/api/custom-reports/${id}`)
export const runCustomReport = (id) => api.post(`/api/custom-reports/${id}/run`)

// Trust accounting — Phase 1 + Phase 2 additions
export const getBankCharges = (trust_account_id) =>
  api.get(`/api/trust/bank-charges${trust_account_id ? `?trust_account_id=${trust_account_id}` : ''}`)
export const createBankCharge = (data) => api.post('/api/trust/bank-charges', data)
export const setDepositDisputed = (id, data) => api.put(`/api/trust/deposits/${id}/disputed`, data)
export const payInvoiceFromTrust = (id, data) => api.post(`/api/invoices/${id}/pay-from-trust`, data)
export const getCtappReport = (year) =>
  api.get(`/api/trust/ctapp-report${year ? `?year=${year}` : ''}`)
export const getComplianceStatus = () => api.get('/api/trust/compliance-status')
export const getRetentionEligible = () => api.get('/api/trust/retention/eligible')
// Phase 3 additions
export const getCtappFilings = () => api.get('/api/trust/ctapp-filings')
export const recordCtappFiling = (data) => api.post('/api/trust/ctapp-filings', data)
export const getStaleRetainers = () => api.get('/api/trust/stale-retainers')
export const getTrustStatements = (trust_account_id) =>
  api.get(`/api/trust/statements${trust_account_id ? `?trust_account_id=${trust_account_id}` : ''}`)
export const uploadTrustStatement = (formData) =>
  api.post('/api/trust/statements', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
export const downloadTrustStatement = (id) =>
  api.get(`/api/trust/statements/${id}/download`, { responseType: 'blob' })
export const deleteTrustStatement = (id) => api.delete(`/api/trust/statements/${id}`)

// Trust accounting — Phase 4 (workflow + remaining State Bar compliance)
export const reverseDeposit = (id, data) => api.post(`/api/trust/deposits/${id}/reverse`, data)
export const reverseDisbursement = (id, data) => api.post(`/api/trust/disbursements/${id}/reverse`, data)
export const createTrustNotification = (data) => api.post('/api/trust/notifications', data)
export const getClientNotifications = (clientId) => api.get(`/api/trust/notifications/client/${clientId}`)
export const getPendingNotifications = () => api.get('/api/trust/notifications/pending')
export const getOutstandingChecks = (trust_account_id) =>
  api.get(`/api/trust/outstanding-checks${trust_account_id ? `?trust_account_id=${trust_account_id}` : ''}`)
export const clearOutstandingChecks = (ids, cleared_date) =>
  api.post('/api/trust/outstanding-checks/clear', { ids, cleared_date })
export const clearDeposit = (id, cleared_date) => api.post(`/api/trust/deposits/${id}/clear`, { cleared_date })
export const getPendingDeposits = (trust_account_id) =>
  api.get(`/api/trust/deposits/pending${trust_account_id ? `?trust_account_id=${trust_account_id}` : ''}`)
export const getDepositTypeBreakdown = () => api.get('/api/trust/by-deposit-type')
export const getAnnualStatement = (clientId, year) =>
  api.get(`/api/trust/annual-statement/${clientId}${year ? `?year=${year}` : ''}`)

// Time entry duplicate detector (admin-only, for dual-system reimport cleanup)
export const getTimeEntryDuplicates = () => api.get('/api/time-entries/duplicates')
export const resolveTimeEntryDuplicates = (delete_ids) =>
  api.post('/api/time-entries/duplicates/resolve', { delete_ids })
// Held import links (Amicus pairings waiting for a person): action is
// 'link' | 'import_copy' | 'keep'; the next Amicus import acts on it.
export const getTimeEntryLinkHolds = () => api.get('/api/time-entries/link-holds')
// shown: for 'link', the Omnia { omnia_date, omnia_hours } on screen — the
// server refuses (409 link_hold_changed) if the entry changed since.
export const resolveTimeEntryLinkHold = (amicusId, action, shown = {}) =>
  api.post(`/api/time-entries/link-holds/${encodeURIComponent(String(amicusId))}/resolve`, { ...shown, action })

// Audit log (admin-only)
export const getAuditLog = (params = {}) => {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v)
  }
  const query = qs.toString()
  return api.get(`/api/audit-log${query ? `?${query}` : ''}`)
}
export const getAuditLogDistinct = (column) =>
  api.get(`/api/audit-log/distinct/${column}`)

// Group → matters bulk visibility management (admin only)
export const getGroupMatters = (groupId) =>
  api.get(`/api/team/groups/${groupId}/matters`)
export const updateGroupMatters = (groupId, data) =>
  api.put(`/api/team/groups/${groupId}/matters`, data)

// Per-matter permission overrides (admin only)
export const listMatterPermissionOverrides = (matterId) =>
  api.get(`/api/matters/${matterId}/permission-overrides`)
export const addMatterPermissionOverride = (matterId, data) =>
  api.post(`/api/matters/${matterId}/permission-overrides`, data)
export const removeMatterPermissionOverride = (matterId, overrideId) =>
  api.delete(`/api/matters/${matterId}/permission-overrides/${overrideId}`)

// Per-matter user blocks (admin only) — hard block on individual users
// (regardless of visibility/overrides).  Backend-enforced via trigger; a
// user can never block themselves nor the matter's responsible attorney.
export const listMatterBlocks = (matterId) =>
  api.get(`/api/matter-blocks/matter/${matterId}`)
export const addMatterBlock = (data) =>
  api.post('/api/matter-blocks', data)
export const removeMatterBlock = (id) =>
  api.delete(`/api/matter-blocks/${id}`)

// Discovery Tracking — Phase 1
export const listDiscoverySets = (params = {}) => {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.append(k, v))
  const s = qs.toString()
  return api.get(`/api/discovery${s ? `?${s}` : ''}`)
}
export const getDiscoverySet = (id) => api.get(`/api/discovery/${id}`)
export const createDiscoverySet = (data) => api.post('/api/discovery', data)
export const updateDiscoverySet = (id, data) => api.put(`/api/discovery/${id}`, data)
export const deleteDiscoverySet = (id) => api.delete(`/api/discovery/${id}`)
export const addDiscoveryExtension = (setId, data) => api.post(`/api/discovery/${setId}/extensions`, data)
export const deleteDiscoveryExtension = (id) => api.delete(`/api/discovery/extensions/${id}`)
export const calculateDiscoveryDeadline = (data) => api.post('/api/discovery/calculate-deadline', data)
// Phase 2 — depositions
export const getDiscoveryDeposition = (setId) => api.get(`/api/discovery/${setId}/deposition`)
export const createDiscoveryDeposition = (setId, data) => api.post(`/api/discovery/${setId}/deposition`, data)
export const updateDiscoveryDeposition = (setId, data) => api.put(`/api/discovery/${setId}/deposition`, data)
export const deleteDiscoveryDeposition = (setId) => api.delete(`/api/discovery/${setId}/deposition`)
// Phase 2 — per-set documents + meet & confer
export const listDiscoveryDocuments = (setId) => api.get(`/api/discovery/${setId}/documents`)
export const listDiscoveryMeetAndConfer = (setId) => api.get(`/api/discovery/${setId}/meet-and-confer`)
// Phase 3 — privilege log (dual-mode: formal CCP §2031.240 OR informal audit trail)
export const listPrivilegeLog = (params = {}) => {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.append(k, v))
  const s = qs.toString()
  return api.get(`/api/discovery/privilege-log${s ? `?${s}` : ''}`)
}
export const getPrivilegeLogEntry = (id) => api.get(`/api/discovery/privilege-log/${id}`)
export const createPrivilegeLogEntry = (data) => api.post('/api/discovery/privilege-log', data)
export const updatePrivilegeLogEntry = (id, data) => api.put(`/api/discovery/privilege-log/${id}`, data)
export const deletePrivilegeLogEntry = (id) => api.delete(`/api/discovery/privilege-log/${id}`)

// Google Calendar Sync
export const getGoogleCalStatus = () => api.get('/api/google-calendar/status')
export const startGoogleCalOAuth = () => api.get('/api/google-calendar/oauth/start')
export const disconnectGoogleCal = () => api.post('/api/google-calendar/disconnect')
export const listGoogleCalendars = () => api.get('/api/google-calendar/calendars')
export const updateGoogleCalPrefs = (data) => api.put('/api/google-calendar/preferences', data)
export const listEligibleGoogleRecipients = () => api.get('/api/google-calendar/eligible-recipients')

// Case Analysis (CaseMap-style fact/issue tracking per matter)
export const listCaseFacts        = (matterId)   => api.get(`/api/case-analysis/matter/${matterId}/facts`)
export const getCaseFact          = (id)         => api.get(`/api/case-analysis/facts/${id}`)
export const createCaseFact       = (data)       => api.post('/api/case-analysis/facts', data)
export const updateCaseFact       = (id, data)   => api.put(`/api/case-analysis/facts/${id}`, data)
export const deleteCaseFact       = (id)         => api.delete(`/api/case-analysis/facts/${id}`)
export const listCaseIssues       = (matterId)   => api.get(`/api/case-analysis/matter/${matterId}/issues`)
export const getCaseIssue         = (id)         => api.get(`/api/case-analysis/issues/${id}`)
export const createCaseIssue      = (data)       => api.post('/api/case-analysis/issues', data)
export const updateCaseIssue      = (id, data)   => api.put(`/api/case-analysis/issues/${id}`, data)
export const deleteCaseIssue      = (id)         => api.delete(`/api/case-analysis/issues/${id}`)
export const linkFactIssue        = (factId, issueId, data = {})    => api.post(`/api/case-analysis/facts/${factId}/issues/${issueId}`, data)
export const unlinkFactIssue      = (factId, issueId)               => api.delete(`/api/case-analysis/facts/${factId}/issues/${issueId}`)
export const linkFactContact      = (factId, contactId, data = {})  => api.post(`/api/case-analysis/facts/${factId}/contacts/${contactId}`, data)
export const unlinkFactContact    = (factId, contactId)             => api.delete(`/api/case-analysis/facts/${factId}/contacts/${contactId}`)
export const linkFactDocument     = (factId, docId, data = {})      => api.post(`/api/case-analysis/facts/${factId}/documents/${docId}`, data)
export const unlinkFactDocument   = (factId, docId)                 => api.delete(`/api/case-analysis/facts/${factId}/documents/${docId}`)
export const getCaseCast          = (matterId)   => api.get(`/api/case-analysis/matter/${matterId}/cast`)
// Phase 3 #1 — Questions spreadsheet
export const listCaseQuestions    = (matterId)   => api.get(`/api/case-analysis/matter/${matterId}/questions`)
export const getCaseQuestion      = (id)         => api.get(`/api/case-analysis/questions/${id}`)
export const createCaseQuestion   = (data)       => api.post('/api/case-analysis/questions', data)
export const updateCaseQuestion   = (id, data)   => api.put(`/api/case-analysis/questions/${id}`, data)
export const deleteCaseQuestion   = (id)         => api.delete(`/api/case-analysis/questions/${id}`)
export const linkQuestionFact     = (qId, fId, data = {}) => api.post(`/api/case-analysis/questions/${qId}/facts/${fId}`, data)
export const unlinkQuestionFact   = (qId, fId)            => api.delete(`/api/case-analysis/questions/${qId}/facts/${fId}`)
// Phase 3 #3 — Saved filters
export const listSavedFilters     = (matterId, scope = 'facts') =>
  api.get(`/api/case-analysis/saved-filters?matter_id=${matterId || ''}&scope=${scope}`)
export const createSavedFilter    = (data) => api.post('/api/case-analysis/saved-filters', data)
export const updateSavedFilter    = (id, data) => api.put(`/api/case-analysis/saved-filters/${id}`, data)
export const deleteSavedFilter    = (id) => api.delete(`/api/case-analysis/saved-filters/${id}`)
// Phase 3 #5 — Custom evaluation fields
export const listEvalFields       = () => api.get('/api/case-analysis/eval-fields')
export const createEvalField      = (data) => api.post('/api/case-analysis/eval-fields', data)
export const updateEvalField      = (id, data) => api.put(`/api/case-analysis/eval-fields/${id}`, data)
export const deleteEvalField      = (id) => api.delete(`/api/case-analysis/eval-fields/${id}`)
export const getFactEvalValues    = (factId) => api.get(`/api/case-analysis/facts/${factId}/eval-values`)
export const setFactEvalValue     = (factId, fieldId, value) =>
  api.put(`/api/case-analysis/facts/${factId}/eval-values/${fieldId}`, { value })
// Phase 4 #2 — Case templates
export const listCaseTemplates    = () => api.get('/api/case-analysis/templates')
export const getCaseTemplate      = (id) => api.get(`/api/case-analysis/templates/${id}`)
export const createCaseTemplate   = (data) => api.post('/api/case-analysis/templates', data)
export const snapshotTemplateFromMatter = (matterId, data) =>
  api.post(`/api/case-analysis/templates/from-matter/${matterId}`, data)
export const applyCaseTemplate    = (matterId, templateId) =>
  api.post(`/api/case-analysis/matter/${matterId}/apply-template/${templateId}`)
export const updateCaseTemplate   = (id, data) => api.put(`/api/case-analysis/templates/${id}`, data)
export const deleteCaseTemplate   = (id) => api.delete(`/api/case-analysis/templates/${id}`)

// Firm-wide broadcast — admin-only.  Fires a WS push to every online user.
// Returns { delivered_to_users, delivered_to_sockets } so the sender can
// see how many people actually received it in real time.
export const sendFirmBroadcast = (message, severity = 'info') =>
  api.post('/api/broadcast', { message, severity })
export const getBroadcastHistory = () => api.get('/api/broadcast/history')

// Per-attorney rate history — admin-only writes; anyone with team-view
// access can read.  Rows sorted newest-first by effective_date.
export const getAttorneyRates    = (userId) => api.get(`/api/team/${userId}/rates`)
export const addAttorneyRate     = (userId, data) => api.post(`/api/team/${userId}/rates`, data)
export const deleteAttorneyRate  = (userId, rateId) => api.delete(`/api/team/${userId}/rates/${rateId}`)

// Custom fields — admin CRUD for reusable field sets on matters + contacts.
// field_type IMMUTABLE post-creation (CRIT-1 panel fix); options soft-
// deactivated never hard-deleted; server mints all option UUIDs.  Backfill
// runs async via worker-lease pattern (CRIT-2) with 30-min lease + reaper.
export const listCustomFieldSets   = (scope) =>
  api.get(`/api/custom-fields/sets${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`)
export const createCustomFieldSet  = (data) => api.post('/api/custom-fields/sets', data)
export const updateCustomFieldSet  = (id, data) => api.put(`/api/custom-fields/sets/${id}`, data)
export const deleteCustomFieldSet  = (id) => api.delete(`/api/custom-fields/sets/${id}`)
export const listCustomFieldDefs   = (setId) =>
  api.get(`/api/custom-fields/defs?set_id=${encodeURIComponent(setId)}`)
export const createCustomFieldDef  = (data) => api.post('/api/custom-fields/defs', data)
export const updateCustomFieldDef  = (id, data) => api.put(`/api/custom-fields/defs/${id}`, data)
export const deleteCustomFieldDef  = (id) => api.delete(`/api/custom-fields/defs/${id}`)
export const triggerBackfill       = (defId) => api.post(`/api/custom-fields/defs/${defId}/backfill`)
export const getBackfillStatus     = (defId) => api.get(`/api/custom-fields/defs/${defId}/backfill-status`)

// Ship 4 — Named Roles.  Admin-only CRUD on `roles` + `user_roles` join.
// Standard roles are protected server-side (DB trigger permits only
// description / is_archived / role_epoch / updated_at changes); archiving
// the only admin-granting role is refused at both route + trigger.
export const listRoles          = () => api.get('/api/roles')
export const getRole            = (id) => api.get(`/api/roles/${id}`)
export const createRole         = (data) => api.post('/api/roles', data)
export const updateRole         = (id, data) => api.put(`/api/roles/${id}`, data)
export const deleteRole         = (id, { force } = {}) =>
  api.delete(`/api/roles/${id}${force ? `?force=${encodeURIComponent(force)}` : ''}`)
export const assignRoleUsers    = (id, user_ids) =>
  api.post(`/api/roles/${id}/assign`, { user_ids })
export const unassignRoleUser   = (id, userId) =>
  api.delete(`/api/roles/${id}/assign/${userId}`)
export const getUserRoles       = (userId) => api.get(`/api/roles/users/${userId}/roles`)
export const setUserRoles       = (userId, role_ids) =>
  api.put(`/api/roles/users/${userId}/roles`, { role_ids })
export const getUserEffectivePerms = (userId) =>
  api.get(`/api/roles/users/${userId}/effective-perms`)

// Ship 6a — Email Templates Library
export const listEmailTemplates      = (params) => api.get('/api/email-templates', { params })
export const getEmailTemplate        = (id) => api.get(`/api/email-templates/${id}`)
export const getEmailTemplateBySlug  = (slug) => api.get(`/api/email-templates/slug/${slug}`)
export const createEmailTemplate     = (data) => api.post('/api/email-templates', data)
export const updateEmailTemplate     = (id, data) => api.put(`/api/email-templates/${id}`, data)
export const deleteEmailTemplate     = (id) => api.delete(`/api/email-templates/${id}`)
export const previewEmailTemplate    = (id, sample_tokens) =>
  api.post(`/api/email-templates/${id}/preview`, { sample_tokens })
export const duplicateEmailTemplate  = (id) => api.post(`/api/email-templates/${id}/duplicate`)
export const resetEmailTemplate      = (id) => api.post(`/api/email-templates/${id}/reset-to-system`)

// Ship 6b — Email Drip Sequences
export const listEmailSequences        = (params) => api.get('/api/email-sequences', { params })
export const getEmailSequence          = (id) => api.get(`/api/email-sequences/${id}`)
export const createEmailSequence       = (data) => api.post('/api/email-sequences', data)
export const updateEmailSequence       = (id, data) => api.put(`/api/email-sequences/${id}`, data)
export const deleteEmailSequence       = (id) => api.delete(`/api/email-sequences/${id}`)
export const duplicateEmailSequence    = (id) => api.post(`/api/email-sequences/${id}/duplicate`)
// Step editor.  IMPORTANT — the backend does NOT nest single-step mutations
// under /:id.  emailSequences.js registers them at the scope ROOT:
//   staff.put   ('/steps/:stepId')
//   staff.delete('/steps/:stepId')
//   staff.post  ('/steps/:stepId/publish-draft')
// Only the COLLECTION routes are nested (`/:id/steps`, `/:id/steps/reorder`).
// These helpers keep `id` in the signature so call sites stay stable (and so
// a future re-nesting is a one-line change here), but it is deliberately NOT
// part of the path for the three single-step verbs.
//
// There is no `GET /:id/steps`; `GET /:id` already returns {sequence, steps},
// so the step list reads from there.
export const listSequenceSteps         = (id) => api.get(`/api/email-sequences/${id}`)
export const addSequenceStep           = (id, data) => api.post(`/api/email-sequences/${id}/steps`, data)
export const updateSequenceStep        = (id, stepId, data) =>
  api.put(`/api/email-sequences/steps/${stepId}`, data)
export const deleteSequenceStep        = (id, stepId) =>
  api.delete(`/api/email-sequences/steps/${stepId}`)
export const reorderSequenceSteps      = (id, step_ids) =>
  api.post(`/api/email-sequences/${id}/steps/reorder`, { step_ids })
export const publishSequenceStep       = (id, stepId) =>
  api.post(`/api/email-sequences/steps/${stepId}/publish-draft`)
// No bulk publish route exists.  Publishing N drafts is N calls to the real
// per-step route, run sequentially so a mid-way failure surfaces the axios
// error (with .response) that callers already render.  Steps published before
// the failure stay published — publish-draft is per-step and independent.
export const publishAllSequenceSteps   = async (id, stepIds = []) => {
  let published = 0
  for (const stepId of stepIds) {
    try {
      await api.post(`/api/email-sequences/steps/${stepId}/publish-draft`)
    } catch (err) {
      // There is no transaction across the N calls, so a mid-way failure
      // leaves the earlier steps LIVE.  Tell the caller how far we got, or it
      // will report "Publish failed" for a run that did publish some steps —
      // and the operator will believe nothing went out when some copy did.
      err.publishedBeforeFailure = published
      throw err
    }
    published += 1
  }
  return { data: { published } }
}
export const enrollLeadsInSequence     = (id, lead_ids) =>
  api.post(`/api/email-sequences/${id}/enroll`, { lead_ids })
// Ship 7b F2.2 audit follow-up (H-L2): the backend endpoint accepts
// `{sample_lead_id?, step_id?}`, not `sample_tokens`.  Callers should pass
// the correct body shape directly.  Kept as a thin passthrough so existing
// call sites can migrate one at a time.  A raw string body (legacy) is
// accepted + ignored so old callers don't blow up.
export const previewEmailSequence      = (id, body) => {
  const payload = (body && typeof body === 'object' && !Array.isArray(body)) ? body : {}
  return api.post(`/api/email-sequences/${id}/preview`, payload)
}
// ⚠ NO BACKEND ROUTE — /stats has no counterpart in emailSequences.js, so it
// 404s.  The path is the correct-by-design target, left in place so adding the
// route is the only change needed.  Callers must degrade honestly on 404
// rather than rendering a generic "load failed" that reads like a blip.
export const getSequenceStats          = (id) => api.get(`/api/email-sequences/${id}/stats`)
// This one DOES exist — staff.get('/enrollments'), emailSequences.js:1130.
// A comment here previously claimed it 404s; that was false, and the claim was
// load-bearing enough that LeadSequencesTab depends on this call working.
// Accepts EITHER lead_id or sequence_id (Ship 6b recovery widened it); 400 if
// neither.  Lead-scoped via leadScopeForCaller.
export const getEnrollmentsForLead     = (lead_id) =>
  api.get('/api/email-sequences/enrollments', { params: { lead_id } })
// ⚠ NO BACKEND ROUTE — /enrollments/:id/sends is not registered; it 404s.
export const getEnrollmentSends        = (enrollmentId) =>
  api.get(`/api/email-sequences/enrollments/${enrollmentId}/sends`)
// Unused — no consumer imports this, and no backend route serves it.
export const getEnrollmentEvents       = (enrollmentId) =>
  api.get(`/api/email-sequences/enrollments/${enrollmentId}/events`)
export const pauseEnrollment           = (enrollmentId, reason) =>
  api.post(`/api/email-sequences/enrollments/${enrollmentId}/pause`, { reason })
export const resumeEnrollment          = (enrollmentId) =>
  api.post(`/api/email-sequences/enrollments/${enrollmentId}/resume`)
export const optOutEnrollment          = (enrollmentId, scope) =>
  api.post(`/api/email-sequences/enrollments/${enrollmentId}/opt-out`, { scope })
// Staff "stop all email to this lead" (Cal. Rule 1.4 — a lead phoning to ask
// off the list).  SHIPPED 2026-09-15: staff.post('/leads/:leadId/global-opt-out')
// at emailSequences.js:1336, with global-opt-in at :1393 for a mis-click.
//
// Until then there was NO working staff-initiated global opt-out, and it could
// not be worked around from the frontend — worth keeping on the record, because
// it is why this route exists rather than a loop over per-enrollment opt-outs:
//   • POST /enrollments/:eid/opt-out hardcodes scope 'sequence' and never reads
//     request.body, so passing {scope:'global'} is silently ignored.
//   • applyGlobalOptOut was reachable only from the PUBLIC token-based
//     /unsubscribe/:token route, and GET /enrollments strips opt_out_token —
//     so staff had no token, and driving a public route would lose actor
//     attribution in the audit trail.
//   • Looping per-enrollment opt-outs stops CURRENT sends but never sets
//     leads.email_opted_out_at, the only thing the engine checks before FUTURE
//     enrollment (emailSequenceEngine.js:202) — silent under-delivery against a
//     promise of "all current and future".
export const globalOptOutLead          = (leadId) =>
  api.post(`/api/email-sequences/leads/${leadId}/global-opt-out`)
export const globalOptInLead           = (leadId) =>
  api.post(`/api/email-sequences/leads/${leadId}/global-opt-in`)

// Ship 6e — Tag library.  Firm-wide colored labels attachable to leads OR
// contacts; junction endpoints live on leads.js / contacts.js so scope +
// perm gates stay local (see backend for cross-firm attach guards).
export const listTags            = () => api.get('/api/tags')
export const createTag           = (data) => api.post('/api/tags', data)
export const updateTag           = (id, data) => api.put(`/api/tags/${id}`, data)
export const deleteTag           = (id) => api.delete(`/api/tags/${id}`)
export const linkLeadTag         = (leadId, tagId) => api.post(`/api/tags/leads/${leadId}/tags/${tagId}`)
export const unlinkLeadTag       = (leadId, tagId) => api.delete(`/api/tags/leads/${leadId}/tags/${tagId}`)
export const linkContactTag      = (contactId, tagId) => api.post(`/api/tags/contacts/${contactId}/tags/${tagId}`)
export const unlinkContactTag    = (contactId, tagId) => api.delete(`/api/tags/contacts/${contactId}/tags/${tagId}`)
// Bulk attach/detach — ONE request for the whole selection rather than N.
// Server evaluates the lead-scope predicate per row and silently skips rows
// the caller can't see, returning { attached, detached, affected_ids,
// requested, skipped_already, skipped_out_of_scope }.  `affected_ids` holds
// only rows that actually changed, which is what the Undo path reverts.
export const bulkTagLeads        = (body) => api.post('/api/tags/leads/bulk-tag', body)
export const bulkTagContacts     = (body) => api.post('/api/tags/contacts/bulk-tag', body)

// Ship 6e — Quick Intake modal.  Staff-authored lead-creation endpoint;
// perm-gated (admin OR can_manage_leads).  Server dedups on email/phone
// within 30 min and returns { duplicate: true, lead_id } if matched.
export const quickCreateLead     = (data) => api.post('/api/leads', data)

// Ship 6e — Matter-value tracking.  Per-lead expected value + notes.
// Touched-flag: undefined preserves, null clears, integer sets.
export const putLeadValue        = (leadId, data) => api.put(`/api/leads/${leadId}/value`, data)

// --- Ship 7j — Phone-intake reviewer workflow (feedback #743) --------------
// Designated decision-maker receives a notification for every phone-intake
// lead and a daily 3pm reminder until it is marked reviewed.  Uniform 404
// on out-of-scope callers (Ship 1 deny-wins parity — no oracle for lead
// existence).  Mark/unmark are idempotent atomic-UPDATE writes; double-clicks
// return the existing state without churn (panel HIGH-2 fold).
//
// markLeadReviewed accepts optional { notes: string } (server caps at 2000
// chars).  Response: { success, reviewer_reviewed_at, reviewer_reviewed_by,
// reviewer_reviewed_by_name }.
// unmarkLeadReviewed is admin-only; server returns 403 for non-admin.
// listPendingReviewLeads is scoped to caller: admin sees firm-wide, the
// designated reviewer sees the queue, everyone else gets 403.
export const markLeadReviewed              = (leadId, data = {}) =>
  api.post(`/api/leads/${leadId}/mark-reviewed`, data)
export const unmarkLeadReviewed            = (leadId) =>
  api.post(`/api/leads/${leadId}/unmark-reviewed`)
export const listPendingReviewLeads        = (params = {}) =>
  api.get('/api/leads/pending-review', { params })
// Dashboard rollup: { count, oldest_days, recent: [{id, first_name, last_name,
// created_at, age_days}] }.  Returns count=0 when queue is empty so the card
// can render its empty state.
export const getPhoneIntakePendingSummary  = () =>
  api.get('/api/dashboard/phone-intake-pending')

// Ship 6.5 Item 1 — Consult bookings (post-consult outcome capture).
// Admin OR can_manage_consult_outcomes writes; admin OR leads reads.
// Filters: { status?, outcome?, attorney_id?, from?, to? } — YYYY-MM-DD dates.
export const listConsultBookings          = (params = {}) =>
  api.get('/api/consult-bookings', { params })
export const getConsultBooking            = (id) =>
  api.get(`/api/consult-bookings/${id}`)
export const updateConsultBookingOutcome  = (id, data) =>
  api.put(`/api/consult-bookings/${id}/outcome`, data)
export const clearConsultBookingOutcome   = (id) =>
  api.delete(`/api/consult-bookings/${id}/outcome`)
// Per-booking reminder opt-out.  { opted_out: boolean, reason?: string }.
// Turning it off also marks any queued-but-unsent reminder 'skipped' server
// side, in the same transaction — the response's queued_reminders_suppressed
// says how many.
export const setConsultBookingReminders   = (id, data) =>
  api.put(`/api/consult-bookings/${id}/reminders`, data)

// Ship 6.5 Item 3 — Lead assignment rules (admin OR can_manage_leads).
// Rules are priority-ordered; conditions is a JSONB matcher; action is
// round_robin (rotates assignee_user_ids) or specific_user.
export const listLeadAssignmentRules      = () => api.get('/api/lead-assignment-rules')
export const getLeadAssignmentRule        = (id) => api.get(`/api/lead-assignment-rules/${id}`)
export const createLeadAssignmentRule     = (data) => api.post('/api/lead-assignment-rules', data)
export const updateLeadAssignmentRule     = (id, data) => api.put(`/api/lead-assignment-rules/${id}`, data)
export const deleteLeadAssignmentRule     = (id) => api.delete(`/api/lead-assignment-rules/${id}`)
// Per-rule fire / fall-through counts over a ?days= window.  The server
// excludes rows tagged source='test' — production audit_log still holds
// LEAD_AUTO_ASSIGN fixtures left behind by Jest from when the suite ran
// against the live database, and they must never render as firm activity.
export const getLeadAssignmentRuleStats   = (days) =>
  api.get('/api/lead-assignment-rules/stats', { params: days ? { days } : {} })
// Dry-run test — body: { sample_lead_id?: number } OR { sample_lead: {...} }.
// Response: { target_rule_id, target_rule_matches_sample, matched_target_rule, chain_result }.
export const testLeadAssignmentRule       = (id, data) =>
  api.post(`/api/lead-assignment-rules/${id}/test`, data)
// Retroactive backfill.  Rules otherwise only fire on NEW leads, so a firm
// that writes its first rule after accumulating a backlog cannot reach it.
// body: { dry_run?: boolean, limit?: number, max_age_days?: number }.
// dry_run DEFAULTS TRUE server-side — committing requires dry_run:false.
// There is no undo, so the UI runs the preview first and confirms separately.
export const backfillLeadAssignment       = (data) =>
  api.post('/api/lead-assignment-rules/backfill', data)

// --- Ship 7a — Chart of Accounts admin CRUD --------------------------------
// Backend mount: /api/accounting.  Every call passes through the firm-scope
// hook (rejects legacy JWTs without a firm_id claim with a "sign in again"
// message) and the accounting-enabled feature-flag check (admin is exempt
// only for the two bootstrap paths: listChartOfAccounts + seedChartOfAccounts).
// Filters accepted by the list endpoint: { type, include_inactive }.
export const listChartOfAccounts   = (params) => api.get('/api/accounting/coa', { params })
export const getChartOfAccount     = (id) => api.get(`/api/accounting/coa/${id}`)
export const createChartOfAccount  = (data) => api.post('/api/accounting/coa', data)
export const updateChartOfAccount  = (id, data) => api.put(`/api/accounting/coa/${id}`, data)
export const deleteChartOfAccount  = (id) => api.delete(`/api/accounting/coa/${id}`)
// Force re-run is safe (advisory-lock + NOT-EXISTS guards make the seed
// idempotent) — pass { force: true } if the admin wants to re-invoke.
export const seedChartOfAccounts   = (opts = {}) =>
  api.post('/api/accounting/coa/seed', null, opts.force ? { params: { force: 'true' } } : undefined)

// --- Ship 7b — Journal endpoints ------------------------------------------
// Mount: /api/accounting/journal.  Inherits firm-scope + accounting-enabled
// gates from the parent accounting scope; every response is scoped to the
// caller's firm_id via JWT claim.
//
// List filters (all optional):
//   from / to               YYYY-MM-DD date range on entry_date
//   entry_type              one of ck_je_entry_type values
//   source_type / source_id source-row filter
//   include_reversed        'true' to include reversed + reversal entries
//   method                  'A' (accrual) | 'C' (cash) — filter by
//                           accounting_method_used stamp
//   page / limit            pagination (default 50, max 200)
//
// Detail returns { entry, lines, reversal_chain }.
//
// postManualJournal — admin OR can_post_manual_journal.  Server hard-blocks
// any line touching LOCKED_JOURNAL_ROLES (trust cash/liability accounts) per
// Cal. Rule 1.15(d)(3); no permission bypasses.
//
// reverseJournalEntry — admin only.  Reason ≥5 chars.  entry_date optional
// (defaults to today per HIGH #6).
export const listJournalEntries    = (params) => api.get('/api/accounting/journal', { params })
export const getJournalEntry       = (id) => api.get(`/api/accounting/journal/${id}`)
export const postManualJournal     = (data) => api.post('/api/accounting/journal/manual', data)
export const reverseJournalEntry   = (id, data) => api.post(`/api/accounting/journal/${id}/reverse`, data)

// --- Ship 7c — Financial Statements ---------------------------------------
// Mount: /api/accounting/statements.  Inherits firm-scope + accounting-enabled
// gates from the parent accounting scope.  All GETs are read-only, wrapped in
// REPEATABLE READ txns server-side (HIGH-4) so multi-query snapshots are
// internally consistent.  Response headers include Cache-Control: no-store
// (MED-12) — v1 has no client caching either.
//
// Query params:
//   balance-sheet:    as_of=YYYY-MM-DD (required), compare_as_of, basis=A|C
//   income-statement: from, to (required), compare_prior_year=true, basis
//   trial-balance:    as_of (required), basis
//   general-ledger:   from, to, account_id (required), cursor (opaque JSON
//                     from previous page's next_cursor), limit (max 500),
//                     include_reversed=true, basis
//
// GL memo redaction (HIGH-6): callers without can_view_journal_memos receive
// memos as the literal '[memo hidden]' string; response includes a
// memos_redacted:true flag so the UI can render the redacted state distinctly.
export const getBalanceSheet       = (params) => api.get('/api/accounting/statements/balance-sheet', { params })
export const getIncomeStatement    = (params) => api.get('/api/accounting/statements/income-statement', { params })
export const getTrialBalance       = (params) => api.get('/api/accounting/statements/trial-balance', { params })
export const getGeneralLedger      = (params) => {
  // Serialize the cursor object as JSON before shipping — the server accepts
  // an opaque JSON string in the `cursor` query param.
  const q = { ...params }
  if (q.cursor && typeof q.cursor === 'object') q.cursor = JSON.stringify(q.cursor)
  return api.get('/api/accounting/statements/general-ledger', { params: q })
}
export const getReconciliationStatus = () => api.get('/api/accounting/statements/reconciliation-status')

// --- Ship 7c — QuickBooks Export ------------------------------------------
// Mount: /api/accounting/export.  Preview is read-only + open to any
// can_view_accounting holder; the actual /quickbooks POST that returns a ZIP
// requires can_manage_accounting (HIGH-6 rationale — full memo detail).
//
// downloadQuickbooksExport + downloadCsvExport return the raw axios response
// as blob so the caller can save-as via URL.createObjectURL + <a download>.
// Server-side row-count cap = 50,000 lines; over-cap returns 413 with a
// narrow-range hint.
export const previewQuickbooksExport = (params) =>
  api.get('/api/accounting/export/quickbooks/preview', { params })
export const downloadQuickbooksExport = (data) =>
  api.post('/api/accounting/export/quickbooks', data, { responseType: 'blob' })
export const downloadCsvExport = (data) =>
  api.post('/api/accounting/export/csv', data, { responseType: 'blob' })

// Ship 7d — Period Close endpoints.  Mount: /api/accounting/period.
// Inherits firm-scope + accounting-enabled gates from the parent
// accounting scope.  Reads (status + history) require can_view_accounting
// OR admin; close requires can_manage_accounting OR admin; reopen
// requires admin.  Retroactive-edit override lives on the mutation
// endpoints themselves (body: {retroactive_override: true,
// retroactive_reason: "..."} — admin only, reason 20-500 chars).
export const getPeriodStatus  = () => api.get('/api/accounting/period/status')
export const listPeriodHistory = (params) => api.get('/api/accounting/period/history', { params })
export const closePeriod      = (data) => api.post('/api/accounting/period/close', data)
export const reopenPeriod     = (data) => api.post('/api/accounting/period/reopen', data)

// Ship 7e — 1099 Vendor Tracking + IRS-Format Export.  Mount:
// /api/accounting/1099.  Inherits firm-scope + accounting-enabled gates
// from the parent accounting scope.  Reads (vendors, report, filings)
// require can_view_accounting OR admin; writes (vendor updates, filing
// creation, reversal) require can_manage_accounting OR admin.
//
// TIN handling: server NEVER returns tin_encrypted or plaintext TIN via
// the API.  Vendor list rows carry `tin_masked` ("XXX-XX-1234") when the
// caller has can_view_accounting; PUT accepts a plaintext `tin` field
// which is normalized (digits only), validated (exactly 9 digits),
// encrypted via TIN_ENCRYPTION_KEY, and stored — the tin_last4 is
// derived server-side (never trust client-supplied last4).
//
// Filing exports (CSV IRIS + FIRE fixed-width) stream text directly
// from the vendor_snapshot JSONB captured at filing time.  Downloads
// are audit-logged (F1099_EXPORT_DL) with IP + user_agent so admins
// see every access.
export const getFirm1099Vendors    = (taxYear) =>
  api.get('/api/accounting/1099/vendors', { params: taxYear ? { tax_year: taxYear } : undefined })
export const updateVendor1099      = (contactId, data) =>
  api.put(`/api/accounting/1099/vendors/${contactId}`, data)
export const getForm1099Report     = (taxYear, opts = {}) => {
  const params = { tax_year: taxYear }
  if (opts.thresholdOverrideCents != null) params.threshold_override_cents = opts.thresholdOverrideCents
  if (opts.includeUnderThreshold) params.include_under = '1'
  return api.get('/api/accounting/1099/report', { params })
}
// Ship 7b F2.2 (2026-08-18) — rewritten to match the actual backend contract.
// Ship 7e shipped 8 endpoints (GET /vendors, /vendors/:id/reveal-tin, PUT
// /vendors/:id, GET /report, GET /filings, POST /export, POST /mark-filed,
// POST /unmark-filed).  Prior helpers referenced /filings/:id subpaths that
// were never registered — every Generate/CSV/FIRE/Reverse button 404'd.
//
// Export shape: /export streams the file bytes with Content-Disposition
// attachment AND auto-inserts a form_1099_filings row on first-export-of-year
// (regenerates the file on subsequent calls without duplicating the row).
export const list1099Filings       = (params) =>
  api.get('/api/accounting/1099/filings', { params })
export const download1099Csv       = (taxYear) =>
  api.post('/api/accounting/1099/export', null, {
    params: { tax_year: taxYear, format: 'csv' },
    responseType: 'blob',
  })
export const download1099Fire      = (taxYear) =>
  api.post('/api/accounting/1099/export', null, {
    params: { tax_year: taxYear, format: 'fire' },
    responseType: 'blob',
  })
// Dry run — builds the REAL file server-side and returns a JSON preview
// (counts, warnings, sha256, byte length, and the first 50 rendered lines
// with TINs masked to last 4) WITHOUT recording a filing.  Deliberately not
// responseType:'blob' — unlike the two helpers above this returns JSON.
//
// Why it exists: POST /export INSERTs filing_kind='original', filed_at=NOW()
// before it streams, so before this flag the only way to SEE the file you
// were about to send the IRS was to file it, and backing that out needed
// POST /unmark-filed with a >=20-char reason and a break_glass audit row.
// The returned sha256 is the exact hash the committing call will record.
export const preview1099Export     = (taxYear, format) =>
  api.post('/api/accounting/1099/export', { dry_run: true }, {
    params: { tax_year: taxYear, format: format === 'fire' ? 'fire' : 'csv', dry_run: '1' },
  })
export const mark1099Filed         = (body) =>
  api.post('/api/accounting/1099/mark-filed', body)
export const unmark1099Filed       = (body) =>
  api.post('/api/accounting/1099/unmark-filed', body)

// --- Ship 7f — Bank Statement Import + Reconciliation ---------------------
// Mount: /api/accounting/bank.  Inherits firm-scope + accounting-enabled
// gates from the parent accounting scope.  Reads (accounts, reconciliations)
// require can_view_accounting OR admin; mutations (upload, match, unmatch,
// ignore, adjust, finalize) require can_manage_accounting OR admin; unfinalize
// + void are admin-only.  Cash-* system_role accounts are automatically
// reconcilable; for a firm-added account, admin ticks Reconcilable in the
// Chart of Accounts editor (PUT /api/accounting/coa/:id accepts
// is_reconcilable on unlocked debit-normal asset rows only).
//
// Multipart upload flow for POST /accounts/:accountId/reconciliations —
// CSV files require date_format + column_map (persisted per-account via
// bank_csv_import_presets).  OFX 1.02 SGML subset only; 2.x/QFX/credit-card
// refused with clear errors.  File size cap 5 MB.
//
// Auto-match returns proposed matches without persisting; caller reviews +
// posts to /apply-matches with explicit confirmed_ids for trust-account
// fuzzy candidates flagged requires_human_confirm.
//
// Finalize atomically stamps every matched journal_line with reconciled_at +
// reconciliation_id (via the whitelist-relaxed immutability trigger) inside
// one TXN; refuses when discrepancy_cents !== 0 (outstanding + needs-posting
// are OK — only unclassified residual gates).
export const listBankAccounts        = () => api.get('/api/accounting/bank/accounts')
export const listBankReconciliations = (params) => api.get('/api/accounting/bank/reconciliations', { params })
export const getBankReconciliation   = (id) => api.get(`/api/accounting/bank/reconciliations/${id}`)
export const getBankCsvPreset        = (accountId) =>
  api.get(`/api/accounting/bank/csv-preset/${accountId}`)
export const saveBankCsvPreset       = (accountId, data) =>
  api.put(`/api/accounting/bank/csv-preset/${accountId}`, data)
export const createBankReconciliation = (accountId, formData) =>
  api.post(`/api/accounting/bank/accounts/${accountId}/reconciliations`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
export const autoMatchBankRec        = (id) =>
  api.post(`/api/accounting/bank/reconciliations/${id}/auto-match`)
export const applyBankMatches        = (id, data) =>
  api.post(`/api/accounting/bank/reconciliations/${id}/apply-matches`, data)
export const matchBankLine           = (recId, lineId, matched_journal_line_id) =>
  api.put(`/api/accounting/bank/reconciliations/${recId}/lines/${lineId}`, { matched_journal_line_id })
// expectedMatchedAt is the row's `matched_at` as the grid received it — an
// optimistic-lock token.  OPTIONAL by contract: omit it and the server behaves
// exactly as before.  Sent as a DELETE body; the server also accepts it as a
// query param for clients that cannot send one.
export const unmatchBankLine         = (recId, lineId, expectedMatchedAt) =>
  api.delete(
    `/api/accounting/bank/reconciliations/${recId}/lines/${lineId}/match`,
    expectedMatchedAt ? { data: { expected_matched_at: expectedMatchedAt } } : undefined
  )
export const ignoreBankLine          = (recId, lineId, reason) =>
  api.put(`/api/accounting/bank/reconciliations/${recId}/lines/${lineId}/ignore`, { reason })
export const postBankAdjustment      = (recId, data) =>
  api.post(`/api/accounting/bank/reconciliations/${recId}/post-adjustment`, data)
export const finalizeBankRec         = (recId) =>
  api.post(`/api/accounting/bank/reconciliations/${recId}/finalize`)
export const unfinalizeBankRec       = (recId, data) =>
  api.post(`/api/accounting/bank/reconciliations/${recId}/unfinalize`, data || {})
export const voidBankReconciliation  = (recId, reason) =>
  api.post(`/api/accounting/bank/reconciliations/${recId}/void`, { reason })

// Ship 7d — Shared helper for extracting a friendly message + payload
// from a PERIOD_LOCKED error.  Route responses use the shape:
//   { error, code:'PERIOD_LOCKED', hint, lock_date, closed_at,
//     closed_by_name, closed_by_id, entry_date, source_table,
//     source_id, override_available, request_id }
// Callers can either surface the toast text OR pop a "Retry with
// override…" modal if `override_available` is true and the caller
// wants that UX.
export function handlePeriodLockedError (err) {
  const data = err && err.response && err.response.data
  if (!data || data.code !== 'PERIOD_LOCKED') return null
  const lockDate = data.lock_date || ''
  const closedBy = data.closed_by_name ? ` by ${data.closed_by_name}` : ''
  const message = lockDate
    ? `This entry is in a locked period (closed through ${String(lockDate).slice(0, 10)}${closedBy}). ${data.override_available ? 'Use admin retroactive override or reopen the period.' : 'Contact an admin for a retroactive edit.'}`
    : (data.hint || data.error || 'Entry is in a locked accounting period.')
  return {
    isPeriodLocked: true,
    message,
    lockDate: lockDate ? String(lockDate).slice(0, 10) : null,
    closedAt: data.closed_at || null,
    closedByName: data.closed_by_name || null,
    closedById: data.closed_by_id || null,
    entryDate: data.entry_date || null,
    sourceTable: data.source_table || null,
    sourceId: data.source_id || null,
    overrideAvailable: !!data.override_available,
    requestId: data.request_id || null,
    hint: data.hint || null,
  }
}

// Ship 7i.1 — Witness List (per matter, CCP §2034 disclosure roster) +
// supersession chain + disclosure-events child.
//
// Backend mount is at /api/matter-witnesses (see api/src/app.js).  Panel
// HIGH-3 (concurrent stale-record) + HIGH-2 (per-field 400 shape) require
// the caller to inspect err.response.data on 409/400 responses:
//   * 409 error='active_witness_exists'          → recommend supersession
//   * 409 error='supersession_target_stale'      → refetch + retry
//   * 409 error='base_stale'                     → refetch + retry
//   * 409 error='row_superseded'                 → route to successor
//   * 400 error='Required fields missing...'     → per-field payload
//
// createMatterWitness accepts either:
//   { matter_id, contact_id, witness_type, ...fields }                        — plain create
//   { matter_id, contact_id, witness_type, ...fields,
//     reclassify_from_witness_id, confirm_supersession: true,
//     base_updated_at? }                                                       — supersession
// include_superseded and include_withdrawn are INDEPENDENT axes on the server
// (a designation can be replaced, pulled, both, or neither), so they are built
// as separate params rather than one "show history" flag.
export const listMatterWitnesses         = (matterId, opts = {}) => {
  const qs = []
  if (opts.includeSuperseded) qs.push('include_superseded=true')
  if (opts.includeWithdrawn) qs.push('include_withdrawn=true')
  return api.get(`/api/matter-witnesses/matter/${matterId}${qs.length ? `?${qs.join('&')}` : ''}`)
}
export const getMatterWitness            = (id) => api.get(`/api/matter-witnesses/${id}`)
export const createMatterWitness         = (data) => api.post('/api/matter-witnesses', data)
export const updateMatterWitness         = (id, data) => api.put(`/api/matter-witnesses/${id}`, data)
export const deleteMatterWitness         = (id) => api.delete(`/api/matter-witnesses/${id}`)
// Soft-delete for a designation DELETE refuses (served / superseded / chained).
// reason is REQUIRED by the server (>= 10 chars) — it is written to the witness
// row and is what a CCP §2034.300 motion asks about later.  Terminal: there is
// no un-withdraw; re-designating is a supersession.
export const withdrawMatterWitness       = (id, reason) =>
  api.post(`/api/matter-witnesses/${id}/withdraw`, { reason })
export const listWitnessDisclosureEvents = (witnessId) =>
  api.get(`/api/matter-witnesses/${witnessId}/disclosure-events`)
export const createWitnessDisclosureEvent = (witnessId, data) =>
  api.post(`/api/matter-witnesses/${witnessId}/disclosure-events`, data)

// ---------------------------------------------------------------------------
// J3 — Statement reminders (Billing depth #3).
//
// Eligibility is NEVER computed client-side.  GET /api/invoices/:id returns
// `can_send_reminder` + `reminder_eligibility.eligible_thresholds`, both
// derived from the same server predicate the POST handler enforces, so the
// button and the API can't disagree at a midnight boundary or after a credit
// memo zeroes the balance.
// ---------------------------------------------------------------------------
export const listInvoiceReminders = (invoiceId) =>
  api.get(`/api/invoices/${invoiceId}/reminders`)
// body: { days_past_due }  — omit to let the server use today's actual value.
export const sendInvoiceReminder = (invoiceId, body = {}) =>
  api.post(`/api/invoices/${invoiceId}/send-reminder`, body)

// ---------------------------------------------------------------------------
// Stripe refunds.
//
// Same contract as the reminders above and for a sharper reason: refunds move
// real money and cannot be undone from this app.  GET /api/invoices/:id returns
// `can_refund` + `refund_eligibility`, both produced by the SAME pure predicate
// (api/src/lib/refundEligibility.js) that POST /stripe-refund enforces.  Render
// the button from `can_refund` and nothing else — a lookalike check here (for
// example testing invoice.payment_method === 'stripe') is wrong: the succeeded
// webhook writes the literal 'Stripe' with a capital S, and only via COALESCE,
// so that column is not a reliable discriminator.
//
// The endpoint deliberately does NOT flip invoices.status.  The charge.refunded
// webhook owns that, so a refund issued from the Stripe dashboard lands
// identically.  Callers must REFETCH after a refund rather than optimistically
// showing 'refunded' — the status arrives asynchronously and may be seconds
// behind, or may not arrive at all if the webhook is misconfigured, which is
// itself something the firm needs to be able to see.
// body: { amount?: dollars (omit for full), reason?: string }
export const stripeRefund = (invoiceId, body = {}) =>
  api.post(`/api/invoices/${invoiceId}/stripe-refund`, body)

// Refund history for one invoice, read from the STRIPE_REFUND audit trail.
// Rows are 'requested' (we asked Stripe) or 'confirmed' (Stripe told us it
// happened); a requested row with no confirmed partner means the webhook never
// landed.
export const listInvoiceRefunds = (invoiceId) =>
  api.get(`/api/invoices/${invoiceId}/refunds`)

// ---------------------------------------------------------------------------
// Potential Client Questionnaire + intake decisions (2026-08-29).
//
// Templates: /api/questionnaire-templates (read = admin OR
// can_manage_intake_forms OR can_manage_leads; write = admin OR
// can_manage_intake_forms).  Lead-scoped questionnaire + decision routes are
// mounted under /api/leads and return a uniform 404 for off-scope leads.
// NO endpoint below ever returns a plaintext token or sign URL — the link
// lives only in the client's email.
// ---------------------------------------------------------------------------
export const listQuestionnaireTemplates    = (params = {}) =>
  api.get('/api/questionnaire-templates', { params })
export const getQuestionnaireTemplate      = (id) => api.get(`/api/questionnaire-templates/${id}`)
export const createQuestionnaireTemplate   = (data) => api.post('/api/questionnaire-templates', data)
export const updateQuestionnaireTemplate   = (id, data) => api.put(`/api/questionnaire-templates/${id}`, data)
// soft delete (is_active=false) by default; { hard: true } removes the row.
export const deleteQuestionnaireTemplate   = (id, { hard = false } = {}) =>
  api.delete(`/api/questionnaire-templates/${id}${hard ? '?hard=true' : ''}`)
export const addQuestionnaireTemplateItem  = (templateId, data) =>
  api.post(`/api/questionnaire-templates/${templateId}/items`, data)
export const updateQuestionnaireTemplateItem = (templateId, itemId, data) =>
  api.put(`/api/questionnaire-templates/${templateId}/items/${itemId}`, data)
export const deleteQuestionnaireTemplateItem = (templateId, itemId) =>
  api.delete(`/api/questionnaire-templates/${templateId}/items/${itemId}`)
// item_ids must be exactly the template's current item set (no dups).
export const reorderQuestionnaireTemplateItems = (templateId, item_ids) =>
  api.post(`/api/questionnaire-templates/${templateId}/items/reorder`, { item_ids })
// Questionnaire v2 typed questions.  A question's `type` is immutable (PUT
// refuses a change); DELETE cascades to its follow-ups (→ dependents_removed);
// reorder takes EVERY question id once, in display (depth-first) order.
export const addQuestionnaireTemplateQuestion = (templateId, data) =>
  api.post(`/api/questionnaire-templates/${templateId}/questions`, data)
export const updateQuestionnaireTemplateQuestion = (templateId, questionId, data) =>
  api.put(`/api/questionnaire-templates/${templateId}/questions/${questionId}`, data)
export const deleteQuestionnaireTemplateQuestion = (templateId, questionId) =>
  api.delete(`/api/questionnaire-templates/${templateId}/questions/${questionId}`)
export const reorderQuestionnaireTemplateQuestions = (templateId, question_ids) =>
  api.post(`/api/questionnaire-templates/${templateId}/questions/reorder`, { question_ids })

// Lead-scoped questionnaire lifecycle.  send/resend/reopen share the per-lead
// per-day cap (5) → 429; an existing active questionnaire → 409 with
// { active_questionnaire_id }.  to_email/override_reason are admin-only.
export const sendLeadQuestionnaire   = (leadId, data) => api.post(`/api/leads/${leadId}/questionnaire/send`, data)
export const listLeadQuestionnaires  = (leadId) => api.get(`/api/leads/${leadId}/questionnaires`)
export const getLeadQuestionnaire    = (leadId, qid) => api.get(`/api/leads/${leadId}/questionnaires/${qid}`)
export const resendLeadQuestionnaire = (leadId, qid, data = {}) => api.post(`/api/leads/${leadId}/questionnaires/${qid}/resend`, data)
export const voidLeadQuestionnaire   = (leadId, qid, reason) => api.post(`/api/leads/${leadId}/questionnaires/${qid}/void`, { reason })
export const reopenLeadQuestionnaire = (leadId, qid, data = {}) => api.post(`/api/leads/${leadId}/questionnaires/${qid}/reopen`, data)
// Questionnaire v2 — the answers are Rule 1.18 confidences.  GET …/:qid
// carries `answers_restricted` (no answers) for staff off the intake team; an
// admin opens them with a break-glass reason in a POST BODY (never a query
// string — the reason can name people and URLs land in access logs).
// apply-contact and legal-hold re-check the same gate, so a break-glass
// opener passes the reason again.
export const breakGlassLeadQuestionnaire = (leadId, qid, breakGlassReason) =>
  api.post(`/api/leads/${leadId}/questionnaires/${qid}/break-glass`, { break_glass_reason: breakGlassReason })
// fields ⊆ ['full_name','email','phone','city_state_zip'] → 400 contact_not_applied {problems:[{field, rule}]}
export const applyLeadQuestionnaireContact = (leadId, qid, fields, { breakGlassReason } = {}) =>
  api.post(`/api/leads/${leadId}/questionnaires/${qid}/apply-contact`, { fields, ...(breakGlassReason ? { break_glass_reason: breakGlassReason } : {}) })
// Admin only; reason >= 10 characters both ways (lifting re-arms the erase clock).
export const setLeadQuestionnaireLegalHold = (leadId, qid, hold, reason, { breakGlassReason } = {}) =>
  api.post(`/api/leads/${leadId}/questionnaires/${qid}/legal-hold`, { hold, reason, ...(breakGlassReason ? { break_glass_reason: breakGlassReason } : {}) })

// Intake decisions — append-only.  approve = admin OR can_convert_leads;
// decline = admin OR can_manage_leads (reversing an approval also needs
// can_convert_leads → 403).  409 once the lead is represented.  400
// { error: 'conflict_override_required' } when the conflict check is
// pending / conflicts_found and no conflict_override_reason was supplied.
export const listLeadDecisions = (leadId) => api.get(`/api/leads/${leadId}/decisions`)
// Verifier A-1: idempotent resend of the current decision's email.  Letter
// mode rotates the sign link (old link dies); direct mode re-mints the portal
// invite; decline re-renders the letter.  Capped at 5 resends per decision.
export const resendDecisionEmail = (leadId, decisionId) => api.post(`/api/leads/${leadId}/decisions/${decisionId}/resend-email`)
export const approveLead       = (leadId, data) => api.post(`/api/leads/${leadId}/decision/approve`, data)
export const declineLead       = (leadId, data) => api.post(`/api/leads/${leadId}/decision/decline`, data)

// Read-only matter card (Attorney tab) — assertMatterAccess, uniform 404.
export const getMatterQuestionnaire = (matterId) => api.get(`/api/matters/${matterId}/questionnaire`)

// (The decline-letter preview reuses the existing getEmailTemplateBySlug helper above.)

// ---------------------------------------------------------------------------
// #815 — staff phone-intake script (2026-08-31).
//
// The checklist a staff member works through WHILE ON THE PHONE with someone
// who is not yet a client.  Distinct from the client-facing questionnaire
// above: that one is emailed and snapshot-immutable, this one is firm-internal
// live-editable columns on `leads` plus a `lead_children` child table.
//
// Two access tiers on ONE endpoint (panel C2, Rule 1.18):
//   • the plain script (county / city / state / case type / facts) rides
//     ordinary lead scope, like any other lead field;
//   • the CPS branch and the children ride a STRICTER predicate — assigned
//     attorney, or explicitly screened in, or an admin who breaks glass with a
//     written reason.  Deny-wins INCLUDING admins.
//
// So the GET response is not uniform.  Always branch on `cps_visible`:
//   cps_visible: true   → cps_first_contact_date / cps_ongoing /
//                         cps_ongoing_venue / children[] / child_count present
//   cps_visible: false  → those keys are ABSENT (not null) and
//                         `cps_restricted_reason` explains why.  Render a
//                         "limited to the intake team" state — NOT an empty
//                         form, which reads as "this family has no children".
// `cps_related` is ALWAYS present either way: that a CPS branch EXISTS is not
// itself confidential (staff need it to route the file); its CONTENTS are.
//
// Do NOT read CPS fields off the plain lead object from GET /api/leads/:id —
// that projection is `SELECT l.*` and still carries them.  Only this endpoint
// applies the predicate and logs the read.
//
// Error shapes worth handling by name:
//   403 + break_glass guidance  → caller is an ADMIN off the need-to-know
//                                 list; retry with break_glass_reason
//                                 (>= 20 chars).  Non-admins never see this.
//   404                         → uniform off-scope answer (house rule 8).
//   400                         → validation; the message names the field, and
//                                 for the CPS branch rule it names every
//                                 answer that must be cleared in the same PUT.
// ---------------------------------------------------------------------------

// `break_glass_reason` rides the QUERY here (the GET has no body); the PUT
// takes it in the body.  Both are logged.
export const getLeadIntakeScript = (leadId, { breakGlassReason } = {}) =>
  api.get(`/api/leads/${leadId}/intake-script`,
    breakGlassReason ? { params: { break_glass_reason: breakGlassReason } } : undefined)

// Touched-flag semantics (house rule 7): OMIT a key to preserve it, send null
// to clear it, send a value to set it.  `children` is REPLACE-THE-WHOLE-SET —
// send the complete array or omit the key entirely; sending a partial array
// deletes the siblings you left out.
//
// The CPS branch rule is enforced server-side against the row as it WILL BE:
// turning `cps_related` off while any CPS answer or child survives is a 400.
// Clear them in the SAME request — see clearCpsBranchPayload() below.
export const updateLeadIntakeScript = (leadId, data) =>
  api.put(`/api/leads/${leadId}/intake-script`, data)

// Marks the interview complete.  400 lists exactly what is still missing
// (first_name, phone-or-email, case_type, facts_narrative).  Deliberately an
// explicit act, not a side effect of the last keystroke: an incomplete script
// must never open a decision path.
export const completeLeadIntakeScript = (leadId) =>
  api.post(`/api/leads/${leadId}/intake-script/complete`, {})

// The "who has viewed this" panel — grants + the append-only read history.
// Readable by admins and by anyone already screened in; seeing the membership
// list is not seeing the records.
export const getLeadProspectAccess = (leadId) =>
  api.get(`/api/leads/${leadId}/prospect-access`)
// Admin only.  Screens a colleague in so they can read the CPS branch and the
// records without breaking glass.
export const grantLeadProspectAccess = (leadId, userId, reason) =>
  api.post(`/api/leads/${leadId}/prospect-access/grants`, { user_id: userId, reason })
// Admin only.  Revoked, never deleted — the row is the evidence that this
// person once had access, which is what a later conflict analysis needs.
export const revokeLeadProspectAccess = (leadId, userId) =>
  api.delete(`/api/leads/${leadId}/prospect-access/grants/${userId}`)

// Build the payload that turns the CPS branch OFF without tripping the
// server's branch rule.  Every CPS answer and the whole child set have to go
// in the same request, or the PUT 400s with "cps_related must be true before
// recording ...".  Exported so any future caller gets this right by default
// rather than rediscovering the constraint at the 400.
export function clearCpsBranchPayload () {
  return {
    cps_related: false,
    cps_first_contact_date: null,
    cps_ongoing: null,
    cps_ongoing_venue: null,
    children: [],
  }
}

// True when a failed intake-script request is the admin break-glass 403 rather
// than an ordinary permission error.  The backend answers off-scope callers
// with a uniform 404, so this can only ever be true for an admin.
export function isBreakGlassRequired (err) {
  const status = err?.response?.status
  if (status !== 403) return false
  const body = err?.response?.data || {}
  return /break_glass_reason/i.test(String(body.error || body.message || ''))
}

// ─── #816 prospective-client record uploads (Cluster C, 2026-08-31) ─────────
// STAFF side.  Every one of these is gated server-side by
// callerCanSeeProspectiveConfidential (deny-wins, including admins): off-scope
// callers get a uniform 404, so a 404 from these is a designed answer, not an
// error to surface loudly.  The plaintext upload token is never returned by any
// of them — it exists only in the client's email.
export const getRecordUploadReadiness = () => api.get('/api/leads/records/readiness')
// data: { to_email?, send_anyway? }.  503 { error: 'uploads_unavailable' } means
// the scanner is down and the link would not work — re-post with send_anyway.
export const sendLeadRecordsLink   = (leadId, data = {}) => api.post(`/api/leads/${leadId}/records-link`, data)
export const revokeLeadRecordsLink = (leadId, reason) => api.post(`/api/leads/${leadId}/records-link/revoke`, { reason })
// `break_glass_reason` rides the QUERY (these are GETs, so there is no body),
// exactly as getLeadIntakeScript does it.  Without a way to SEND the reason the
// server's 403 was a dead end: an admin off the need-to-know list was told to
// retry with a reason by an API the client had no parameter for.
export const listLeadRecords       = (leadId, { breakGlassReason } = {}) =>
  api.get(`/api/leads/${leadId}/records`,
    breakGlassReason ? { params: { break_glass_reason: breakGlassReason } } : undefined)
// Attachment-only, server-side gated.  responseType blob means an ERROR body
// arrives as an ArrayBuffer — decode it before reading .message.
export const downloadLeadRecord    = (leadId, fileId, { breakGlassReason } = {}) =>
  api.get(`/api/leads/${leadId}/records/${fileId}/download`, {
    responseType: 'blob',
    ...(breakGlassReason ? { params: { break_glass_reason: breakGlassReason } } : {}),
  })
// The sha256 echo is not ceremony: it keys the review to the exact bytes, so a
// swapped file cannot inherit a review it never had (panel D8).
export const markLeadRecordReviewed = (leadId, fileId, sha256) =>
  api.post(`/api/leads/${leadId}/records/${fileId}/reviewed`, { sha256 })
export const setLeadRecordLegalHold = (leadId, fileId, hold, reason) =>
  api.post(`/api/leads/${leadId}/records/${fileId}/legal-hold`, { hold, reason })

// NOTE: there is deliberately no helper for the PUBLIC upload surface.
// frontend/src/pages/RecordUpload.js talks to /api/public/lead-records/* with
// plain fetch + XHR, because this axios instance attaches the staff Bearer
// token and its 401 interceptor redirects to /login — both wrong for a page
// whose whole point is that the visitor has no account.


export default api
